import * as core from '@easterngraphics/wcf/modules/core';
import * as cf from '@easterngraphics/wcf/modules/cf';
import tippy, { Instance } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { translateLocalStorage } from '../../helpers/i18n';
import { runtimeConfig } from '../../helpers/runtime-config';

const copyModule = import('clipboard-copy');

export class ControlUI {
    private readonly coreApp: core.Application;
    private readonly articleManager: cf.ArticleManager;

    constructor(coreApp: core.Application, articleManager: cf.ArticleManager) {
        this.coreApp = coreApp;
        this.articleManager = articleManager;
        document.addEventListener('property-editor:updated', () => {
            void this.buildUI();
        });
    }

    private async buildUI(): Promise<void> {
        const container = document.getElementById('property-editor') as HTMLDivElement | null;
        if (container == null) {
            return;
        }

        container.querySelector('#dnt-controls')?.remove();

        const mainArticle = this.articleManager.getAllMainArticles()[0];
        if (mainArticle == null) {
            return;
        }

        await mainArticle.getArticleData().catch((error) => {
            console.error('Failed to read variant code', error);
        });

        const item: HTMLDivElement = document.createElement('div');
        item.id = 'dnt-controls';
        item.className = 'dnt-controls';
        item.innerHTML = `
            <button type="button" id="link-copy">
                ${translateLocalStorage('copyConfigLink')}
            </button>
        `;

        container.insertBefore(item, container.firstChild);
        const copyLinkButton = item.querySelector<HTMLButtonElement>('#link-copy');

        if (copyLinkButton == null) {
            return;
        }

        const shareUploadUrl = runtimeConfig.shareUploadUrl;
        if (shareUploadUrl == null) {
            copyLinkButton.disabled = true;
            copyLinkButton.title = translateLocalStorage('shareUnavailable');
            return;
        }

        const tooltip = tippy(copyLinkButton, {
            theme: 'custom',
            placement: 'top',
            content: translateLocalStorage('copied'),
            trigger: 'manual',
        });

        copyLinkButton.addEventListener('click', () => {
            void this.copyUrl()
                .then(() => this.showTooltip(tooltip))
                .catch((error) => {
                    console.error('Failed to copy share url', error);
                    window.alert(translateLocalStorage('shareFailed'));
                });
        });
    }

    private async cacheObx(): Promise<string> {
        const articles = this.articleManager.getAllMainArticles();
        const obxUrl = await this.articleManager.exportObx(articles);
        const shareUploadUrl = runtimeConfig.shareUploadUrl;
        if (shareUploadUrl == null) {
            throw new Error('shareUploadUrl missing');
        }

        try {
            const response = await fetch(obxUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch obx payload: ${response.status}`);
            }

            const payload = await response.text();
            const uploadResponse = await fetch(shareUploadUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/xml'
                },
                body: payload
            });

            if (!uploadResponse.ok) {
                throw new Error(`Share upload failed: ${uploadResponse.status}`);
            }

            const responseText = await uploadResponse.text();
            const token = this.extractShareToken(responseText);
            if (token == null) {
                throw new Error('Share upload succeeded but no token was returned');
            }
            return token;
        } finally {
            await this.articleManager.session.deleteFile(obxUrl);
        }
    }

    private async buildUrl(): Promise<string> {
        const shareToken = await this.cacheObx();
        const url = new URL(window.location.href);
        url.searchParams.set('selection', shareToken);
        return `${url.origin}${url.pathname}?${url.searchParams.toString()}${runtimeConfig.anchor}`;
    }

    private async copyUrl(): Promise<void> {
        await this.copyText(await this.buildUrl());
    }

    private async copyText(value: string): Promise<void> {
        const clipboardModule = await copyModule;
        const clipboardCopy = ('default' in clipboardModule ? clipboardModule.default : clipboardModule) as (text: string) => Promise<void>;
        await clipboardCopy(value);
    }

    private extractShareToken(responseText: string): string | null {
        try {
            const parsed = JSON.parse(responseText) as Record<string, unknown>;
            const candidate = parsed.success ?? parsed.id ?? parsed.token ?? parsed.selection;
            return typeof candidate === 'string' && candidate.length > 0 ? candidate : null;
        } catch {
            const trimmed = responseText.trim();
            return trimmed.length > 0 ? trimmed : null;
        }
    }

    private showTooltip(instance: Instance): void {
        instance.show();
        window.setTimeout(() => instance.hide(), 1000);
    }
}
